import { FC, useEffect, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import BackgroundBox from '../../components/custom_components/BackgroundBox'
import PerFlexRow from '../../components/custom_components/FlexRow'
import PerIcon from '../../components/custom_components/Icon'
import PerSpacer from '../../components/custom_components/Spacer'
import PerText from '../../components/custom_components/Text'
import { useAuth } from '../../context/auth-context'
import { useEmployeeCloseAppraisalMeeting } from '../../graphql/meeting/hooks/useEmployeeCloseAppraisalMeeting'
import { useMeetingPdfStatus } from '../../graphql/meeting/hooks/useMeetingPdfStatus'
import { useObjectivesWithAttachmentsFromMeeting } from '../../graphql/objectives/useObjectivesWithAttachmentsFromMeeting'
import { useDateTime } from '../../shared/useDateTime'
import { useHistory } from 'react-router'
import {
  EmployeeCloseMeetingInput,
  MeetingDashboardFragment,
  MeetingPdfStatus,
  MeetingStatus,
  MeetingType,
  ObjectiveAttachmentInfoDto,
} from '../../types'
import { EmployeeCloseMeetingModal } from '../dashboard/Tiles/Employee/EmployeeCloseMeetingModal'
import { getConclusionHrefByMeeting, getPreparationPdfHrefByMeeting } from '../meeting/util/hrefUtils'
import './EmployeeMeetingStatusBox.less'

type EmployeeMeetingStatusBoxProps = {
  meeting: MeetingDashboardFragment
}

export const EmployeeMeetingStatusBox: FC<EmployeeMeetingStatusBoxProps> = ({ meeting }) => {
  const [open, setOpen] = useState<boolean>(false)
  const { employeeCloseMeeting, loading: isLoading } = useEmployeeCloseAppraisalMeeting()
  const { data: objectiveData } = useObjectivesWithAttachmentsFromMeeting(meeting.id)
  const objectivesFromMeeting: ObjectiveAttachmentInfoDto[] = !!objectiveData ? objectiveData.objectivesWithAttachmentsFromMeeting : []

  const { data, startPolling, stopPolling } = useMeetingPdfStatus(meeting.id)

  const shouldPollingBeEnabled = (meeting: MeetingDashboardFragment) => {
    if (meeting.status === MeetingStatus.PendingConfirmation && meeting.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess)
      // manager closed meeting and pdf is not generated yet
      return true
    else if (
      (meeting.status === MeetingStatus.Draft || meeting.status === MeetingStatus.Prepared) &&
      meeting.employeePrepared &&
      meeting.preparationPdfStatus !== MeetingPdfStatus.FinishedSuccess
    ) {
      //employee prepared and preparation pdf is not generated yet
      return true
    } else {
      return false
    }
  }

  useEffect(() => {
    let handleTimeout: number
    if (shouldPollingBeEnabled(meeting)) {
      startPolling(5000)
      handleTimeout = window.setTimeout(() => {
        stopPolling()
      }, 30000)
    } else {
      stopPolling()
    }
    return () => window.clearTimeout(handleTimeout)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [meeting])

  const conclusionPdfLoaded: boolean =
    meeting.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess ||
    (data ? data.meetingPdfStatus.conclusionPdfStatus === MeetingPdfStatus.FinishedSuccess : false)
  const preparationPdfLoaded: boolean =
    meeting.preparationPdfStatus === MeetingPdfStatus.FinishedSuccess ||
    (data ? data.meetingPdfStatus.preparationPdfStatus === MeetingPdfStatus.FinishedSuccess : false)

  const preparationDownloadHref: undefined | string = getPreparationPdfHrefByMeeting(meeting)
  const conclusionDownloadHref: undefined | string = getConclusionHrefByMeeting(meeting)

  const setConfirmModalOpen = () => {
    setOpen(true)
  }

  const closeMeeting = async (meetingInput: EmployeeCloseMeetingInput) => {
    const closeMeetingResult = await employeeCloseMeeting(meetingInput)
    if (closeMeetingResult && !closeMeetingResult.errors) {
      setOpen(false)
    }
  }

  return (
    <>
      <EmployeeCloseMeetingModal
        meeting={meeting}
        visible={open}
        closeModal={() => setOpen(false)}
        onCloseMeeting={closeMeeting}
        objectivesWithAttachments={objectivesFromMeeting}
        loading={isLoading}
      />

      <BackgroundBox mb="m" r height="messageBox" noPadding>
        <PerFlexRow align="middle" noWrap className="fullHeight" mr="l" ml="l">
          <MeetingStatusActionContent
            setConfirmModalOpen={setConfirmModalOpen}
            meeting={meeting}
            preparationPdfLoaded={preparationPdfLoaded}
            conclusionPdfLoaded={conclusionPdfLoaded}
            preparationDownloadHref={preparationDownloadHref}
            conclusionDownloadHref={conclusionDownloadHref}
          />
        </PerFlexRow>
      </BackgroundBox>
    </>
  )
}

const MeetingStatusActionContent: FC<{
  setConfirmModalOpen: () => void
  meeting: MeetingDashboardFragment
  preparationPdfLoaded: boolean
  conclusionPdfLoaded: boolean
  preparationDownloadHref: string | undefined
  conclusionDownloadHref: string | undefined
}> = ({ setConfirmModalOpen, meeting, preparationPdfLoaded, conclusionPdfLoaded, preparationDownloadHref, conclusionDownloadHref }) => {
  const [t] = useTranslation()
  const { user } = useAuth()
  const history = useHistory()

  const { formatDateShort } = useDateTime(user)
  const iconSize = 24
  const preparationPdfNotFinished = !preparationPdfLoaded
  const conclusionPdfNotFinished = !conclusionPdfLoaded

  const typeOfMeeting = t(`meetingTypes.${meeting.type}`)
  const dateOfMeeting = formatDateShort(meeting.displayDate)
  const reviewerFullName = meeting.reviewer && `${meeting.reviewer.firstName} ${meeting.reviewer.lastName}`

  switch (meeting.status) {
    case MeetingStatus.Prepared:
    case MeetingStatus.Draft: {
      if (!meeting.employeePrepared) {
        return (
          <>
            <PerIcon name="edit_stroke" size={iconSize} color="warning" variant="decoration" />
            <PerSpacer ml="xs" mr="xs" />
            <PerFlexRow direction="column">
              <PerText fontSize="m" color="default" mb="xxxs">
                <Trans
                  i18nKey="ownGrowthView.meetingStatus.startPreparation.actionMessage"
                  values={{ typeOfMeeting }}
                  components={[
                    <div
                      className="color-warning"
                      style={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
                      onClick={(event) => {
                        event.preventDefault()
                        history.push('/employeePrepareMeeting/' + meeting.id)
                      }}
                    />,
                  ]}
                />
              </PerText>
              <PerText color="secondary" fontSize="m">
                {t('ownGrowthView.meetingStatus.startPreparation.secondaryMessage', { reviewerFullName, dateOfMeeting })}
              </PerText>
            </PerFlexRow>
          </>
        )
      } else {
        if (
          meeting.preparationPdfStatus === MeetingPdfStatus.FinishedError ||
          meeting.preparationPdfFileName === 'error.pdf' ||
          (!preparationPdfNotFinished &&
            (!preparationDownloadHref ||
              preparationDownloadHref === 'noPreparationSnapshot.pdf' ||
              preparationDownloadHref === 'error.pdf')) ||
          (meeting.preparationPdfStatus === MeetingPdfStatus.NotStarted &&
            (meeting.preparationPdfFileName === 'noPreparationSnapshot.pdf' || meeting.preparationPdfFileName === 'error.pdf'))
        ) {
          return (
            <>
              <PerIcon name="viewNo_stroke" size={iconSize} color="secondary" variant="decoration" />
              <PerSpacer ml="xs" mr="xs" />
              <PerFlexRow direction="column">
                <PerText fontSize="m" color="default" mb="xxxs">
                  {t('dashboard.employee.meetings.pdfGenerationError')}
                </PerText>
                <PerText color="secondary" fontSize="m">
                  {t('ownGrowthView.meetingStatus.startPreparation.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                </PerText>
              </PerFlexRow>
            </>
          )
        }
        return (
          <>
            {preparationPdfNotFinished ? (
              <>
                <PerIcon name="loading_fill" size={iconSize} spin color="secondary" variant="decoration" />
                <PerSpacer ml="xs" mr="xs" />
                <PerFlexRow direction="column">
                  <PerText fontSize="m" color="default" mb="xxxs">
                    {t('dashboard.employee.meetings.pdfGenerationInProgress')}
                  </PerText>
                  <PerText color="secondary" fontSize="m">
                    {t('ownGrowthView.meetingStatus.viewPreparation.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                  </PerText>
                </PerFlexRow>
              </>
            ) : (
              <>
                <PerIcon name="view_stroke" size={iconSize} color="info" variant="decoration" />
                <PerSpacer ml="xs" mr="xs" />
                <PerFlexRow direction="column">
                  <PerText fontSize="m" color="default" mb="xxxs">
                    <Trans
                      i18nKey="ownGrowthView.meetingStatus.viewPreparation.actionMessage"
                      values={{ typeOfMeeting }}
                      components={[
                        <a
                          className="color-info"
                          style={{ textDecoration: 'underline' }}
                          href={preparationDownloadHref ? preparationDownloadHref : '#'}
                        >
                          {''}
                        </a>,
                      ]}
                    />
                  </PerText>
                  <PerText color="secondary" fontSize="m">
                    {t('ownGrowthView.meetingStatus.viewPreparation.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                  </PerText>
                </PerFlexRow>
              </>
            )}
          </>
        )
      }
    }

    case MeetingStatus.PendingConfirmation: {
      if (meeting.type === MeetingType.Appraisal || meeting.type === MeetingType.AppraisalUpdate) {
        if (
          meeting.conclusionPdfFileName === 'error.pdf' ||
          meeting.conclusionPdfStatus === MeetingPdfStatus.FinishedError ||
          (!conclusionPdfNotFinished &&
            (!conclusionDownloadHref || conclusionDownloadHref === 'noPreparationSnapshot.pdf' || conclusionDownloadHref === 'error.pdf'))
        ) {
          return (
            <>
              <PerIcon name="viewNo_stroke" size={iconSize} color="secondary" variant="decoration" />
              <PerSpacer ml="xs" mr="xs" />
              <PerFlexRow direction="column">
                <PerText fontSize="m" color="default" mb="xxxs">
                  {t('dashboard.employee.meetings.pdfGenerationError')}
                </PerText>
                <PerText color="secondary" fontSize="m">
                  {t('ownGrowthView.meetingStatus.viewAndConfirm.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                </PerText>
              </PerFlexRow>
            </>
          )
        }
        return (
          <>
            {conclusionPdfNotFinished ? (
              <>
                <PerIcon name="loading_fill" size={iconSize} spin color="secondary" variant="decoration" />
                <PerSpacer ml="xs" mr="xs" />
                <PerFlexRow direction="column">
                  <PerText fontSize="m" color="default" mb="xxxs">
                    {t('dashboard.employee.meetings.pdfGenerationInProgress')}
                  </PerText>
                  <PerText color="secondary" fontSize="m">
                    {t('ownGrowthView.meetingStatus.viewAndConfirm.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                  </PerText>
                </PerFlexRow>
              </>
            ) : (
              <>
                <PerIcon name="view_stroke" size={iconSize} color="success" variant="decoration" />
                <PerSpacer ml="xs" mr="xs" />
                <PerFlexRow direction="column">
                  <PerText fontSize="m" color="default" mb="xxxs">
                    <Trans
                      i18nKey="ownGrowthView.meetingStatus.viewAndConfirm.actionMessage"
                      values={{ typeOfMeeting }}
                      components={[
                        <div
                          className="color-success"
                          style={{ cursor: 'pointer', textDecoration: 'underline', display: 'inline' }}
                          onClick={setConfirmModalOpen}
                        />,
                      ]}
                    />
                  </PerText>
                  <PerText color="secondary" fontSize="m">
                    {t('ownGrowthView.meetingStatus.viewAndConfirm.secondaryMessage', { reviewerFullName, dateOfMeeting })}
                  </PerText>
                </PerFlexRow>
              </>
            )}
          </>
        )
      }
      return null
    }

    case MeetingStatus.DisputedConfirmed: {
      if (meeting.type === MeetingType.Appraisal || meeting.type === MeetingType.AppraisalUpdate) {
        if (meeting.conclusionPdfStatus !== MeetingPdfStatus.FinishedSuccess) {
          return (
            <>
              <PerIcon name="viewNo_stroke" size={iconSize} color="secondary" variant="decoration" />
              <PerSpacer ml="xs" mr="xs" />
              <PerFlexRow direction="column">
                <PerText fontSize="m" color="default" mb="xxxs">
                  {t('dashboard.employee.meetings.pdfGenerationError')}
                </PerText>
                <PerText color="secondary" fontSize="m">
                  {t('ownGrowthView.meetingStatus.disputedMeeting.secondaryMessage', { dateOfMeeting })}
                </PerText>
              </PerFlexRow>
            </>
          )
        } else {
          return (
            <>
              <PerIcon name="view_stroke" size={iconSize} color="error" variant="decoration" />
              <PerSpacer ml="xs" mr="xs" />
              <PerFlexRow direction="column">
                <PerText fontSize="m" color="default" mb="xxxs">
                  <Trans
                    i18nKey="ownGrowthView.meetingStatus.disputedMeeting.actionMessage"
                    values={{ typeOfMeeting }}
                    components={[
                      <a
                        className="color-error"
                        style={{ textDecoration: 'underline' }}
                        href={conclusionDownloadHref ? conclusionDownloadHref : '#'}
                      >
                        {''}
                      </a>,
                    ]}
                  />
                </PerText>
                <PerText color="secondary" fontSize="m">
                  {t('ownGrowthView.meetingStatus.disputedMeeting.secondaryMessage', { dateOfMeeting })}
                </PerText>
              </PerFlexRow>
            </>
          )
        }
      }
      return null
    }

    default:
      return null
  }
}
